import React, { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { HOLIDAY_TYPE_OPTIONS } from './shared/hrmsPolicyOptions';

const { Text } = Typography;

const HolidaysPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [calendarId, setCalendarId] = useState<string>();
  const [calDrawer, setCalDrawer] = useState(false);
  const [dayDrawer, setDayDrawer] = useState(false);
  const [calForm] = Form.useForm();
  const [dayForm] = Form.useForm();

  const { data: calendars = [], isLoading: calLoading } = useQuery({
    queryKey: hrmsKeys.holidayCalendars(),
    queryFn: () => hrmsService.listHolidayCalendars(),
  });

  const { data: days = [], isLoading: daysLoading } = useQuery({
    queryKey: hrmsKeys.holidayDays(calendarId || ''),
    queryFn: () => hrmsService.listHolidayDays(calendarId!),
    enabled: Boolean(calendarId),
  });

  const selected = calendars.find((c: any) => c.id === calendarId);

  const saveCalendar = async () => {
    try {
      const v = await calForm.validateFields();
      await hrmsService.createHolidayCalendar(v);
      message.success('Calendar created');
      setCalDrawer(false);
      calForm.resetFields();
      queryClient.invalidateQueries({ queryKey: hrmsKeys.holidayCalendars() });
    } catch {
      message.error('Failed to create calendar');
    }
  };

  const saveDay = async () => {
    if (!calendarId) return;
    try {
      const v = await dayForm.validateFields();
      await hrmsService.createHolidayDay(calendarId, {
        ...v,
        holiday_date: v.holiday_date.format('YYYY-MM-DD'),
      });
      message.success('Holiday added');
      setDayDrawer(false);
      dayForm.resetFields();
      queryClient.invalidateQueries({ queryKey: hrmsKeys.holidayDays(calendarId) });
    } catch {
      message.error('Failed to add holiday');
    }
  };

  const deleteDay = async (id: string) => {
    if (!calendarId) return;
    try {
      await hrmsService.deleteHolidayDay(calendarId, id);
      message.success('Deleted');
      queryClient.invalidateQueries({ queryKey: hrmsKeys.holidayDays(calendarId) });
    } catch {
      message.error('Delete failed');
    }
  };

  const dayColumns: ColumnsType<any> = [
    {
      title: 'Date',
      dataIndex: 'holiday_date',
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    { title: 'Name', dataIndex: 'holiday_name' },
    {
      title: 'Type',
      dataIndex: 'holiday_type',
      render: (t: string) => HOLIDAY_TYPE_OPTIONS.find((o) => o.value === t)?.label.split(' (')[0] || t,
    },
    {
      title: 'Paid',
      dataIndex: 'is_paid',
      render: (v: boolean) => (v ? 'Paid' : 'Unpaid'),
    },
    {
      title: '',
      key: 'a',
      width: 90,
      render: (_, r) => (
        <Button danger size="small" type="link" onClick={() => deleteDay(r.id)}>
          Delete
        </Button>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Holiday calendars"
          subtitle="Public and company holidays used by attendance, leave, and payroll calendars"
          primaryActions={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCalDrawer(true)}>
              New calendar
            </Button>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Why calendars matter"
        description={
          <span>
            Mark one calendar as <strong>Default</strong> for the company year. Holiday days exclude attendance
            expectations and inform leave day counts. Align policy work week under{' '}
            <Link to="/hr/setup">HRMS Setup</Link>.
          </span>
        }
      />

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Card title="Calendars" loading={calLoading}>
            {calendars.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No calendars yet — create one for the current year"
              />
            ) : (
              <>
                <Select
                  style={{ width: '100%', marginBottom: 12 }}
                  placeholder="Select calendar"
                  value={calendarId}
                  onChange={setCalendarId}
                  options={calendars.map((c: any) => ({
                    value: c.id,
                    label: `${c.calendar_name} (${c.calendar_year})${c.is_default ? ' · default' : ''}`,
                  }))}
                />
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={calendars}
                  columns={[
                    { title: 'Code', dataIndex: 'calendar_code' },
                    { title: 'Year', dataIndex: 'calendar_year', width: 70 },
                    {
                      title: 'Default',
                      dataIndex: 'is_default',
                      width: 70,
                      render: (v: boolean) => (v ? 'Yes' : ''),
                    },
                  ]}
                  onRow={(r) => ({
                    onClick: () => setCalendarId(r.id),
                    style: {
                      cursor: 'pointer',
                      background: r.id === calendarId ? '#f0f5ff' : undefined,
                    },
                  })}
                />
              </>
            )}
          </Card>
        </Col>
        <Col xs={24} md={16}>
          <Card
            title={selected ? `Holiday days — ${selected.calendar_name}` : 'Holiday days'}
            extra={
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                disabled={!calendarId}
                onClick={() => setDayDrawer(true)}
              >
                Add day
              </Button>
            }
          >
            {!calendarId ? (
              <Empty description="Select a calendar on the left to manage holiday days" />
            ) : (
              <Table
                rowKey="id"
                loading={daysLoading}
                dataSource={days}
                columns={dayColumns}
                pagination={{ pageSize: 15 }}
                locale={{ emptyText: 'No holidays in this calendar yet' }}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Drawer title="New holiday calendar" open={calDrawer} onClose={() => setCalDrawer(false)} width={420} destroyOnClose>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="One default calendar per year"
          description="Use a short code (e.g. UAE-2026). Only one calendar should be Default for payroll."
        />
        <Form form={calForm} layout="vertical">
          <Form.Item
            name="calendar_code"
            label="Code"
            rules={[{ required: true }]}
            help="Short unique code, e.g. UAE-2026"
          >
            <Input placeholder="UAE-2026" />
          </Form.Item>
          <Form.Item name="calendar_name" label="Display name" rules={[{ required: true }]} help="Shown in lists and reports">
            <Input placeholder="UAE Public Holidays 2026" />
          </Form.Item>
          <Form.Item name="calendar_year" label="Calendar year" rules={[{ required: true }]} initialValue={dayjs().year()}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="is_default"
            label="Company default"
            valuePropName="checked"
            help="Attendance and leave use the default calendar when none is assigned"
          >
            <Switch />
          </Form.Item>
          <Button type="primary" block onClick={saveCalendar}>
            Create calendar
          </Button>
        </Form>
      </Drawer>

      <Drawer title="Add holiday day" open={dayDrawer} onClose={() => setDayDrawer(false)} width={420} destroyOnClose>
        <Form form={dayForm} layout="vertical" initialValues={{ holiday_type: 'Public', is_paid: true }}>
          <Form.Item name="holiday_date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="holiday_name" label="Holiday name" rules={[{ required: true }]}>
            <Input placeholder="E.g. Eid Al Fitr" />
          </Form.Item>
          <Form.Item name="holiday_type" label="Type" help="Classifies the day for reporting; Public is the usual national holiday">
            <Select options={[...HOLIDAY_TYPE_OPTIONS]} />
          </Form.Item>
          <Form.Item
            name="is_paid"
            label="Paid holiday"
            valuePropName="checked"
            help="Paid holidays do not deduct from leave balances when marked as holiday attendance"
          >
            <Switch checkedChildren="Paid" unCheckedChildren="Unpaid" />
          </Form.Item>
          <Button type="primary" block onClick={saveDay}>
            Add holiday
          </Button>
        </Form>
      </Drawer>
    </WorkspaceLayout>
  );
};

export default HolidaysPage;
